import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Activity, MousePointerClick, FilePlus2, ArrowDown, Sparkles } from 'lucide-react';
interface SlideCTALiftProps {
  onContinue: () => void;
}
// Slide 7 — From Engagement to Prescription
// Simple three-step narrative: a moderate lift on engagement and CTA
// is what drives the measurable behaviours behind Rx impact.
export function SlideCTALift({
  onContinue
}: SlideCTALiftProps) {
  const steps = [{
    icon: <Activity className="w-5 h-5" data-id="element-159" />,
    label: 'Engagement Rate',
    caption: 'HCPs who interact with the campaign',
    before: '15%',
    after: '18%',
    lift: '+3 pts after 4 campaigns'
  }, {
    icon: <MousePointerClick className="w-5 h-5" data-id="element-160" />,
    label: 'CTA Conversion',
    caption: 'High-value actions taken after engaging',
    before: '8%',
    after: '11%',
    lift: '+3 pts after 4 campaigns'
  }, {
    icon: <FilePlus2 className="w-5 h-5" data-id="element-161" />,
    label: 'Rx Impact',
    caption: 'Modelled commercial uplift — indication-dependent',
    before: 'baseline',
    after: '+2%',
    lift: 'estimated Rx effect'
  }];
  const highValueActions = ['Requested rep follow-up', 'Opened clinical resource', 'Viewed product evidence', 'Registered for webinar', 'Used patient identification content', 'Clicked into dosing / treatment pathway material'];
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-12 max-w-7xl mx-auto" data-id="element-162">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-163">
        <span className="text-teal-400 text-xs uppercase tracking-widest font-medium" data-id="element-164">
          From Engagement to Prescription
        </span>
        <h2 className="text-3xl md:text-5xl font-serif text-white mt-3 mb-3" data-id="element-165">
          Engagement lifts CTA. CTA lifts Rx.{' '}
          <span className="text-teal-400" data-id="element-166">It compounds.</span>
        </h2>
        <p className="text-base text-white/60 max-w-3xl" data-id="element-167">
          We don't promise prescriptions. We improve the measurable behaviours
          that lead to them — and the data shows where to push next.
        </p>
      </motion.div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 mt-8" data-id="element-168">
        {/* LEFT — Three-step cause→effect→outcome flow */}
        <div className="lg:col-span-7 flex flex-col gap-2" data-id="element-169">
          {steps.map((step, idx) => <Fragment key={step.label} data-id="element-170">
              <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.15
          }} className={`rounded-2xl p-5 border ${idx === 2 ? 'bg-teal-400/10 border-teal-400/40' : 'bg-navy-900/50 border-navy-700'}`} data-id="element-171">
                <div className="flex items-center justify-between gap-4" data-id="element-172">
                  <div className="flex items-center gap-3 min-w-0" data-id="element-173">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${idx === 2 ? 'bg-teal-400/20 text-teal-400 border border-teal-400/40' : 'bg-teal-400/10 border border-teal-400/20 text-teal-400'}`} data-id="element-174">
                      {step.icon}
                    </div>
                    <div className="min-w-0" data-id="element-175">
                      <div className="text-white font-medium" data-id="element-176">{step.label}</div>
                      <div className="text-white/40 text-[11px] truncate" data-id="element-177">
                        {step.caption}
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0" data-id="element-178">
                    <div className="flex items-baseline gap-2 justify-end" data-id="element-179">
                      <span className="text-white/40 line-through text-sm" data-id="element-180">
                        {step.before}
                      </span>
                      <span className="text-3xl md:text-4xl font-serif text-teal-400 tracking-tight" data-id="element-181">
                        {step.after}
                      </span>
                    </div>
                    <div className="text-teal-300/70 text-[10px] uppercase tracking-widest font-medium" data-id="element-182">
                      {step.lift}
                    </div>
                  </div>
                </div>
              </motion.div>

              {idx < steps.length - 1 && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: idx * 0.15 + 0.1
          }} className="flex justify-center py-0.5" data-id="element-183">
                  <ArrowDown className="w-4 h-4 text-teal-400/50" data-id="element-184" />
                </motion.div>}
            </Fragment>)}

          <p className="text-white/60 text-sm border-l-2 border-teal-400/40 pl-3 py-1 mt-3" data-id="element-185">
            We don't claim every click becomes a prescription.{' '}
            <span className="text-white font-medium" data-id="element-186">
              We improve the behaviours that make prescriptions more likely.
            </span>
          </p>
        </div>

        {/* RIGHT — High Value Actions panel */}
        <div className="lg:col-span-5" data-id="element-187">
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3
        }} className="bg-navy-900/70 border border-teal-400/30 rounded-2xl p-6 h-full" data-id="element-188">
            <div className="flex items-center gap-2 mb-1" data-id="element-189">
              <Sparkles className="w-4 h-4 text-teal-400" data-id="element-190" />
              <p className="text-teal-300 text-[10px] uppercase tracking-widest font-medium" data-id="element-191">
                High value actions
              </p>
            </div>
            <h3 className="text-white font-serif text-xl mb-2" data-id="element-192">
              What CTA conversion actually means
            </h3>
            <p className="text-white/50 text-xs mb-5" data-id="element-193">
              Concrete HCP behaviours that signal prescribing intent — measured
              per campaign, per country, per brand.
            </p>

            <ul className="space-y-3" data-id="element-194">
              {highValueActions.map((action, i) => <motion.li key={action} initial={{
              opacity: 0,
              x: 10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.4 + i * 0.06
            }} className="flex items-start gap-3 bg-navy-800/40 border border-navy-700 rounded-lg px-3 py-2.5" data-id="element-195">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-400 mt-2 flex-shrink-0" data-id="element-196" />
                  <span className="text-white/85 text-sm leading-snug" data-id="element-197">
                    {action}
                  </span>
                </motion.li>)}
            </ul>

            <p className="text-white/35 text-[10px] italic leading-relaxed mt-5" data-id="element-198">
              Rx impact is modelled by Roche based on indication, market access
              and follow-up. Snakker provides the behavioural evidence.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="flex justify-end mt-6" data-id="element-199">
        <Button onClick={onContinue} data-id="element-200">See the operating model →</Button>
      </div>
    </div>;
}